// @ts-nocheck

import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { SportTemplate } from '@/components/sports-hubs/SportTemplate';
import { Loader2 } from 'lucide-react';

interface Sport {
  id: string;
  name: string;
  gender?: string;
  active: boolean;
}

interface SportsHubContent {
  id: string;
  sport_id: string;
  thumbnail_image_url: string | null;
  hero_image_url: string | null;
  description: string | null;
  is_active: boolean;
  sport: Sport;
}

const SportHubPage: React.FC = () => {
  const { sportSlug } = useParams<{ sportSlug: string }>();
  const [sportsHubContent, setSportsHubContent] = useState<SportsHubContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSportHub = async () => {
      if (!sportSlug) {
        setError('Sport slug is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Parse the slug to extract sport name and gender
        let sportName: string;
        let gender: string | undefined;
        
        if (sportSlug.startsWith('men-') || sportSlug.startsWith('women-')) {
          const parts = sportSlug.split('-');
          gender = parts[0] === 'men' ? 'Men' : 'Women';
          sportName = parts.slice(1).join(' ');
        } else {
          sportName = sportSlug.replace(/-/g, ' ');
        }

        // Capitalize sport name properly
        sportName = sportName.split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');

        console.log('🔍 Looking for sport:', { sportName, gender, originalSlug: sportSlug });

        // Query the sports_hub_content table with sport data
        let query = supabase
          .from('sports_hub_content')
          .select(`
            id,
            sport_id,
            thumbnail_image_url,
            hero_image_url,
            description,
            is_active,
            sports!inner(
              id,
              name,
              gender,
              active
            )
          `)
          .eq('is_active', true)
          .eq('sports.name', sportName);

        if (gender) {
          query = query.eq('sports.gender', gender);
        }

        const { data, error: fetchError } = await query.maybeSingle();

        if (fetchError) {
          console.error('❌ Error fetching sport hub:', fetchError);
          throw fetchError;
        }

        console.log('✅ Sport hub query result:', data);

        if (!data) {
          console.log('❌ Sport hub not found:', { sportName, gender });
          setError('Sport hub not found');
          setIsLoading(false);
          return;
        }

        setSportsHubContent({
          ...data,
          sport: Array.isArray(data.sports) ? data.sports[0] : data.sports
        });
        setError(null);
      } catch (err: any) {
        console.error('Error fetching sport hub:', err);
        setError(err.message || 'Failed to load sport hub');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSportHub();
  }, [sportSlug]);

  // Generate sport description based on sport name
  const getSportDescription = (sportName: string): string => {
    const descriptions: { [key: string]: string } = {
      'Basketball': 'Elite basketball recruiting and development opportunities',
      'Football': 'Premier football recruiting and scholarship opportunities',
      'Baseball': 'Top-tier baseball recruiting and development programs',
      'Softball': 'Elite softball recruiting and athletic opportunities',
      'Soccer': 'Premier soccer recruiting and development programs',
      'Track and Field': 'Elite track and field recruiting opportunities',
      'Swimming': 'Premier swimming and diving recruiting programs',
      'Tennis': 'Elite tennis recruiting and development opportunities',
      'Golf': 'Top-tier golf recruiting and scholarship programs',
      'Volleyball': 'Elite volleyball recruiting and athletic opportunities',
      'Wrestling': 'Premier wrestling recruiting and development programs',
      'Cross Country': 'Elite cross country recruiting opportunities',
      'Lacrosse': 'Premier lacrosse recruiting and development programs',
      'Field Hockey': 'Elite field hockey recruiting opportunities',
      'Water Polo': 'Premier water polo recruiting programs'
    };

    return descriptions[sportName] || `Elite ${sportName.toLowerCase()} recruiting and development opportunities`;
  };

  // Generate hero image based on sport
  const getSportHeroImage = (sportName: string): string => {
    const images: { [key: string]: string } = {
      'Basketball': 'https://images.pexels.com/photos/5384617/pexels-photo-5384617.jpeg',
      'Football': 'https://images.pexels.com/photos/1618200/pexels-photo-1618200.jpeg',
      'Baseball': 'https://images.pexels.com/photos/264337/pexels-photo-264337.jpeg',
      'Softball': 'https://images.pexels.com/photos/1409999/pexels-photo-1409999.jpeg',
      'Soccer': 'https://images.pexels.com/photos/46798/the-ball-stadion-football-the-pitch-46798.jpeg',
      'Track and Field': 'https://images.pexels.com/photos/936094/pexels-photo-936094.jpeg',
      'Swimming': 'https://images.pexels.com/photos/863988/pexels-photo-863988.jpeg',
      'Tennis': 'https://images.pexels.com/photos/209977/pexels-photo-209977.jpeg',
      'Golf': 'https://images.pexels.com/photos/54123/pexels-photo-54123.jpeg',
      'Volleyball': 'https://images.pexels.com/photos/2116469/pexels-photo-2116469.jpeg',
      'Wrestling': 'https://images.pexels.com/photos/6393367/pexels-photo-6393367.jpeg',
      'Cross Country': 'https://images.pexels.com/photos/2402777/pexels-photo-2402777.jpeg',
      'Lacrosse': 'https://images.pexels.com/photos/163254/sport-lacrosse-ball-game-163254.jpeg',
      'Field Hockey': 'https://images.pexels.com/photos/3621104/pexels-photo-3621104.jpeg',
      'Water Polo': 'https://images.pexels.com/photos/863988/pexels-photo-863988.jpeg'
    };

    return images[sportName] || 'https://images.pexels.com/photos/5384617/pexels-photo-5384617.jpeg';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-secondary" />
            <span className="ml-3 text-lg font-semibold">Loading Sport Hub...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !sportsHubContent) {
    return <Navigate to="/sports-hubs" replace />;
  }

  const sport = sportsHubContent.sport;
  const displayName = sport.gender && sport.gender !== 'Co-Ed' 
    ? `${sport.gender}'s ${sport.name}` 
    : sport.name;

  // Use uploaded hero image if available, otherwise fallback to default
  const heroImage = sportsHubContent.hero_image_url || getSportHeroImage(sport.name);
  const description = sportsHubContent.description || getSportDescription(sport.name);

  return (
    <SportTemplate
      sportName={displayName}
      heroImage={heroImage}
      sportDescription={description}
    />
  );
};

export default SportHubPage;